/**
 * Handles submission for the contact page form with improved feedback logic.
 */
export const initPageContactForm = (): void => {
  const formWrapper = document.querySelector<HTMLDivElement>(
    ".contact-form-column .form-wrapper"
  );
  if (!formWrapper) {
    return;
  }

  const form = formWrapper.querySelector<HTMLFormElement>(".app-contact-form");
  if (!form) {
    return;
  }

  const messagesContainer = formWrapper.querySelector<HTMLDivElement>(
    ".form-messages-container"
  );
  const formContentWrapper = formWrapper.querySelector<HTMLDivElement>(
    ".form-content-wrapper"
  );

  if (!messagesContainer || !formContentWrapper) {
    console.warn("[Contact Form] Required wrapper elements not found.");
    return;
  }

  const successTpl =
    window.themeVariables?.formTranslations?.contactForm?.status
      ?.successMessage || "<strong>Success!</strong> Message sent.";
  const errorTpl =
    window.themeVariables?.formTranslations?.contactForm?.status
      ?.errorMessage || "<strong>Error!</strong> Message not sent.";

  const createAlert = (type: "success" | "error", message: string): string => {
    const icon = type === "success" ? "#check" : "#close";
    return `
      <div class="alert alert--${type} alert--filled">
        <div class="alert__icon">
          <svg class="icon"><use xlink:href="${icon}"></use></svg>
        </div>
        <p class="alert__text">${message}</p>
        <span class="alert__close">
          <svg class="icon"><use xlink:href="#close"></use></svg>
        </span>
      </div>
    `;
  };

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const submitButton = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    );
    if (!submitButton) return;

    // 1. Clear previous messages and disable the button
    messagesContainer.innerHTML = "";
    submitButton.disabled = true;
    submitButton.style.opacity = "0.7";

    // Safety check for the localized nonce
    if (!window.themeVariables?.pageContactFormNonce) {
      console.error("Page contact form nonce not found.");
      messagesContainer.innerHTML = createAlert(
        "error",
        "A configuration error occurred. Please try again."
      );
      submitButton.disabled = false;
      submitButton.style.opacity = "1";
      return;
    }

    const formData = new FormData(form);
    formData.append("action", "submit_page_contact_form");
    // Append the nonce from our localized themeVariables object
    formData.append(
      "contactFormNonce",
      window.themeVariables.pageContactFormNonce
    );

    try {
      const response = await fetch(window.themeVariables.ajaxUrl, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        // SUCCESS: Display success message and hide the form
        const message = result.data?.message || successTpl;
        messagesContainer.innerHTML = createAlert("success", message);
        form.style.display = "none"; // Hide the form
      } else {
        // ERROR: Display error message
        const message = result.data?.message || errorTpl;
        messagesContainer.innerHTML = createAlert("error", message);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      messagesContainer.innerHTML = createAlert("error", errorTpl);
    } finally {
      // Re-enable the button ONLY on error
      if (form.style.display !== "none") {
        submitButton.disabled = false;
        submitButton.style.opacity = "1";
      }

      // Add a single event listener to the container for the close button
      const closeBtn = messagesContainer.querySelector(".alert__close");
      if (closeBtn) {
        closeBtn.addEventListener(
          "click",
          () => {
            messagesContainer.innerHTML = "";
          },
          { once: true }
        );
      }
    }
  });

  console.log("[Forms] Page Contact Form initialized with updated logic.");
};
